/**
 * Canonical builders for public-facing fcat URLs (menu, package, quote, order).
 *
 * Any code that generates a shareable link — admin "Copy Link" buttons today,
 * future edge functions that email/SMS links tomorrow — should use these
 * helpers instead of hand-concatenating `window.location.origin` + a path.
 *
 * Source of truth is the `VITE_PUBLIC_APP_URL` env var (e.g.
 * `https://app.tristanoscatering.ca`). If it's unset we fall back to
 * `window.location.origin` with a dev-only console warning so local dev
 * without the var keeps working but production misconfigs get noticed.
 */

let warned = false;

function resolveBase(): string {
  const raw = import.meta.env.VITE_PUBLIC_APP_URL as string | undefined;
  if (raw && raw.trim()) return raw.trim().replace(/\/$/, "");

  if (typeof window !== "undefined") {
    if (!warned && import.meta.env.DEV) {
      console.warn(
        "[public-urls] VITE_PUBLIC_APP_URL is not set — falling back to window.location.origin. " +
          "Set it in .env (local) or Vercel project settings (preview/prod)."
      );
      warned = true;
    }
    return window.location.origin.replace(/\/$/, "");
  }

  // SSR / edge / test contexts with neither a window nor the env var.
  return "";
}

export function publicMenuUrl(slug: string): string {
  return `${resolveBase()}/menu/${encodeURIComponent(slug)}`;
}

export function publicPackageUrl(slug: string): string {
  return `${resolveBase()}/packages/${encodeURIComponent(slug)}`;
}

export function publicQuoteUrl(id: string): string {
  return `${resolveBase()}/quote/${encodeURIComponent(id)}`;
}

export function publicPackagesBrowseUrl(): string {
  return `${resolveBase()}/packages`;
}

export function publicOrderUrl(): string {
  return `${resolveBase()}/order`;
}

/** Expose the base URL for use in JSON-LD and other SEO contexts. */
export function publicBaseUrl(): string {
  return resolveBase();
}
